import React from 'react';

import type { NormalizedQuote } from '../../../apis/lanePlanner';
import { BookingFormValues, needsOwnWaybill, PartyForm } from './bookingForm';
import styles from './styles.module.css';

const DASH = '—';

const Line: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={styles.detailRow}>
    <span>{label}</span>
    <span className={styles.factValue}>{value.trim() || DASH}</span>
  </div>
);

const ReviewCard: React.FC<{
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}> = ({ title, onEdit, children }) => (
  <section className={styles.bookingCard} aria-label={title}>
    <div className={styles.bookingCardHead}>
      <h4 className={styles.bookingCardTitle}>{title}</h4>
      <button
        type='button'
        className={styles.linkButton}
        aria-label={`Edit ${title}`}
        onClick={onEdit}
      >
        Edit
      </button>
    </div>
    {children}
  </section>
);

const PartyLines: React.FC<{ party: PartyForm; addressLabel: string }> = ({
  party,
  addressLabel,
}) => (
  <>
    <Line label='Company' value={party.company} />
    <Line label={addressLabel} value={party.address} />
    <Line label='Contact' value={party.contact} />
    <Line label='Facility hours' value={party.facilityHours} />
  </>
);

interface BookingReviewStepProps {
  quote: NormalizedQuote;
  values: BookingFormValues;
  submitting: boolean;
  error: string | null;
  onEdit: () => void;
  onConfirm: () => void;
}

const BookingReviewStep: React.FC<BookingReviewStepProps> = ({
  quote,
  values,
  submitting,
  error,
  onEdit,
  onConfirm,
}) => (
  <div>
    <ReviewCard title='Shipper' onEdit={onEdit}>
      <PartyLines party={values.shipper} addressLabel='Pickup address' />
    </ReviewCard>
    <ReviewCard title='Consignee' onEdit={onEdit}>
      <PartyLines party={values.consignee} addressLabel='Delivery address' />
    </ReviewCard>
    <ReviewCard title='Commodity' onEdit={onEdit}>
      <Line label='Description' value={values.description} />
      <Line label='PO / reference' value={values.poReference} />
      <Line label='Pickup number' value={values.pickupNumber} />
      <Line label='Notes' value={values.handlingNotes} />
      {needsOwnWaybill(quote) && (
        <Line
          label='Air waybill'
          value={`${values.waybillPrefix}-${values.waybillNumber}`}
        />
      )}
    </ReviewCard>
    <div className={styles.partialNote}>
      By confirming, you agree to the carrier’s terms. The booking is placed
      with the vendor right away.
    </div>
    {error && (
      <div className={styles.errorBanner} role='alert'>
        {error}
      </div>
    )}
    <div className={styles.bookingActions}>
      <button
        type='button'
        className={styles.secondaryAction}
        disabled={submitting}
        onClick={onEdit}
      >
        Back
      </button>
      <button
        type='button'
        className={styles.primaryAction}
        disabled={submitting}
        onClick={onConfirm}
      >
        {submitting ? 'Booking…' : 'Confirm booking'}
      </button>
    </div>
  </div>
);

export default BookingReviewStep;
